import { dataCollector } from './dataCollector';
import { oceanAnalysisEngine } from './oceanAnalysisEngine';
import { 
  ReportRequest, 
  FishingReport, 
  ProcessingStatus,
  OceanDataSet 
} from './types';
import * as Crypto from 'expo-crypto';

export class ReportGeneratorService {
  private static instance: ReportGeneratorService;
  private processingQueue: Map<string, ProcessingStatus> = new Map();
  private reportStorage: Map<string, FishingReport> = new Map();
  
  private constructor() {}
  
  static getInstance(): ReportGeneratorService {
    if (!ReportGeneratorService.instance) {
      ReportGeneratorService.instance = new ReportGeneratorService();
    }
    return ReportGeneratorService.instance;
  }
  
  async generateReport(request: ReportRequest): Promise<string> {
    const reportId = Crypto.randomUUID();
    
    this.updateStatus(reportId, {
      reportId,
      status: 'queued',
      progress: 0,
      currentStep: 'Initializing report generation'
    });
    
    setTimeout(() => this.processReport(reportId, request), 0);
    
    return reportId;
  }
  
  private async processReport(reportId: string, request: ReportRequest): Promise<void> {
    const startTime = Date.now();
    
    try {
      this.updateStatus(reportId, {
        reportId,
        status: 'processing',
        progress: 10,
        currentStep: 'Collecting ocean data'
      });
      
      const oceanData = await dataCollector.collectOceanData(request);
      
      this.updateStatus(reportId, {
        reportId,
        status: 'processing',
        progress: 40,
        currentStep: 'Validating data quality'
      });
      
      const validation = await dataCollector.validateDataQuality(oceanData);
      if (!validation.isValid && validation.completeness < 30) {
        throw new Error(`Insufficient data quality: ${validation.issues.join(', ')}`);
      }
      
      this.updateStatus(reportId, {
        reportId,
        status: 'analyzing',
        progress: 50,
        currentStep: 'Analyzing ocean features'
      });
      
      const analysis = await oceanAnalysisEngine.analyzeOceanData(
        oceanData,
        request.homePort,
        request.waypointOptions
      );
      
      this.updateStatus(reportId, {
        reportId,
        status: 'generating',
        progress: 80,
        currentStep: 'Generating fishing report'
      });
      
      const report = await this.compileReport(
        reportId,
        request,
        oceanData,
        analysis,
        validation,
        startTime
      );
      
      this.storeReport(reportId, report);
      
      this.updateStatus(reportId, {
        reportId,
        status: 'complete',
        progress: 100,
        currentStep: 'Report ready'
      });
      
    } catch (error) {
      console.error('[ReportGenerator] Error processing report:', error);
      this.updateStatus(reportId, {
        reportId,
        status: 'error',
        progress: 0,
        currentStep: 'Failed to generate report',
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  private async compileReport(
    reportId: string,
    request: ReportRequest,
    oceanData: OceanDataSet,
    analysis: any,
    validation: any,
    startTime: number
  ): Promise<FishingReport> {
    const { features, hotspots } = analysis;
    
    const executiveSummary = this.generateExecutiveSummary(features, hotspots, oceanData);
    const oceanConditions = this.analyzeOceanConditions(features, oceanData);
    const recommendations = this.generateRecommendations(hotspots, request.targetSpecies);
    
    return {
      reportId,
      generatedAt: new Date(),
      requestParams: request,
      validityPeriod: {
        start: request.dateRange.start,
        end: new Date(request.dateRange.start.getTime() + 3 * 24 * 60 * 60 * 1000)
      },
      
      executiveSummary,
      oceanConditions,
      
      fishingPredictions: {
        hotspots: hotspots.slice(0, 30),
        avoidAreas: []
      },
      
      recommendations,
      
      dataQuality: {
        cloudCoverage: 0,
        dataAge: Math.floor((Date.now() - oceanData.timestamp.getTime()) / (1000 * 60 * 60)),
        resolution: `${oceanData.sst.resolution}km`,
        completeness: validation.completeness
      },
      
      metadata: {
        processingTime: Date.now() - startTime,
        dataSources: ['NOAA ERDDAP', 'CoastWatch', 'VIIRS', 'Sentinel-3'],
        algorithms: ['Eddy Detection', 'Front Analysis', 'Convergence Mapping'],
        version: '1.0.0'
      }
    };
  }
  
  private generateExecutiveSummary(features: any[], hotspots: any[], oceanData: OceanDataSet): any {
    const keyFindings: string[] = [];
    
    const eddyCount = features.filter(f => f.type === 'eddy').length;
    const frontCount = features.filter(f => f.type === 'front').length;
    const convergenceCount = features.filter(f => f.type === 'convergence').length;
    
    if (eddyCount > 0) {
      keyFindings.push(`${eddyCount} active eddies detected in the analysis area`);
    }
    
    if (frontCount > 0) {
      keyFindings.push(`${frontCount} temperature fronts identified`);
    }
    
    if (convergenceCount > 0) {
      keyFindings.push(`${convergenceCount} convergence zones with bait potential`);
    }
    
    if (hotspots.length > 0) {
      keyFindings.push(`${hotspots.length} high-confidence fishing locations identified`);
    }
    
    const avgTemp = this.calculateAverageTemperature(oceanData.sst);
    keyFindings.push(`Average water temperature: ${(avgTemp * 9/5 + 32).toFixed(1)}°F`);
    
    return {
      overall: this.generateOverallSummary(features, hotspots),
      keyFindings,
      confidence: this.calculateOverallConfidence(features, hotspots)
    };
  }
  
  private analyzeOceanConditions(features: any[], oceanData: OceanDataSet): any {
    const tempTrend = this.analyzeTempTrend(oceanData.sst);
    const currentStrength = this.analyzeCurrentStrength(oceanData.currents);
    
    return {
      summary: this.generateConditionsSummary(features, oceanData),
      features: features.slice(0, 20),
      trends: {
        temperature: tempTrend,
        productivity: 'stable',
        currentStrength
      }
    };
  }
  
  private generateRecommendations(hotspots: any[], targetSpecies?: string[]): any {
    const primary: string[] = [];
    const bySpecies = new Map<string, string[]>();
    
    if (hotspots.length > 0) {
      primary.push(`Focus on the top ${Math.min(3, hotspots.length)} identified hotspots`);
    }
    
    const allSpecies = new Set<string>();
    hotspots.forEach(h => h.targetSpecies.forEach((s: string) => allSpecies.add(s)));
    
    allSpecies.forEach(species => {
      const recommendations: string[] = [];
      
      switch (species) {
        case 'Mahi Mahi':
          recommendations.push('Use ballyhoo or squid as bait');
          recommendations.push('Troll near weed lines and debris');
          break;
        case 'Tuna':
          recommendations.push('Use cedar plugs or daisy chains');
          recommendations.push('Focus on temperature breaks');
          break;
        case 'Wahoo':
          recommendations.push('High-speed trolling with wire leader');
          recommendations.push('Target dawn and dusk periods');
          break;
      }
      
      bySpecies.set(species, recommendations);
    });
    
    return {
      primary,
      bySpecies,
      tackle: ['Medium to heavy tackle recommended', 'Wire leaders for toothy species'],
      safety: ['Monitor weather conditions', 'Check for small craft advisories']
    };
  }
  
  private updateStatus(reportId: string, status: ProcessingStatus): void {
    this.processingQueue.set(reportId, status);
    console.log(`[ReportGenerator] ${reportId}: ${status.currentStep} (${status.progress}%)`);
  }
  
  private storeReport(reportId: string, report: FishingReport): void {
    // React Native doesn't have window.localStorage, use AsyncStorage instead
    // For now, store in memory
    if (!this.reportStorage) {
      this.reportStorage = new Map<string, FishingReport>();
    }
    this.reportStorage.set(reportId, report);
    console.log(`[ReportGenerator] Report stored with ID: ${reportId}`);
  }
  
  async getReportStatus(reportId: string): Promise<ProcessingStatus | null> {
    return this.processingQueue.get(reportId) || null;
  }
  
  async getReport(reportId: string): Promise<FishingReport | null> {
    // Return from memory storage
    const report = this.reportStorage?.get(reportId) || null;
    console.log(`[ReportGenerator] Retrieved report ${reportId}:`, report ? 'Found' : 'Not found');
    return report;
  }
  
  private calculateAverageTemperature(sst: any): number {
    let sum = 0;
    let count = 0;
    const temps = [];
    
    for (const row of sst.grid) {
      for (const val of row) {
        if (val !== null && !isNaN(val)) {
          sum += val;
          count++;
          temps.push(val);
        }
      }
    }
    
    if (count > 0) {
      const avg = sum / count;
      console.log(`[ReportGenerator] Temperature stats: avg=${avg.toFixed(1)}°C, min=${Math.min(...temps).toFixed(1)}°C, max=${Math.max(...temps).toFixed(1)}°C, count=${count}`);
      return avg;
    }
    
    console.log('[ReportGenerator] No temperature data found, using default');
    return 20;
  }
  
  private analyzeTempTrend(sst: any): 'warming' | 'cooling' | 'stable' {
    return 'stable';
  }
  
  private analyzeCurrentStrength(currents: any): 'strengthening' | 'weakening' | 'stable' {
    const avgSpeed = this.calculateAverageSpeed(currents.speedGrid);
    if (avgSpeed > 0.5) return 'strengthening';
    if (avgSpeed < 0.2) return 'weakening';
    return 'stable';
  }
  
  private calculateAverageSpeed(speedGrid: number[][]): number {
    let sum = 0;
    let count = 0;
    
    for (const row of speedGrid) {
      for (const val of row) {
        if (val !== null) {
          sum += val;
          count++;
        }
      }
    }
    
    return count > 0 ? sum / count : 0;
  }
  
  private generateOverallSummary(features: any[], hotspots: any[]): string {
    if (hotspots.length === 0) {
      return 'Ocean conditions are currently suboptimal for fishing in the selected area.';
    }
    
    if (hotspots.length > 5) {
      return 'Excellent fishing conditions detected with multiple high-confidence hotspots identified.';
    }
    
    return 'Good fishing conditions present with several promising locations identified.';
  }
  
  private calculateOverallConfidence(features: any[], hotspots: any[]): number {
    if (hotspots.length === 0) return 0.3;
    
    const avgConfidence = hotspots.reduce((sum, h) => sum + h.confidence, 0) / hotspots.length;
    return Math.min(avgConfidence * 1.2, 0.95);
  }
  
  private generateConditionsSummary(features: any[], oceanData: OceanDataSet): string {
    const parts: string[] = [];
    
    const avgTemp = this.calculateAverageTemperature(oceanData.sst);
    parts.push(`Water temperatures averaging ${(avgTemp * 9/5 + 32).toFixed(1)}°F`);
    
    const eddyCount = features.filter(f => f.type === 'eddy').length;
    if (eddyCount > 0) {
      parts.push(`${eddyCount} eddies creating upwelling conditions`);
    }
    
    const frontCount = features.filter(f => f.type === 'front').length;
    if (frontCount > 0) {
      parts.push(`${frontCount} temperature fronts concentrating baitfish`);
    }
    
    return `${parts.join('. ')  }.`;
  }
}

export const reportGeneratorService = ReportGeneratorService.getInstance();